/**
 * Round template modal — the per-role configuration behind every final round:
 * who interviews, how long, who sits in the single get-to-know block, an
 * optional hiring manager, and an optional recruiter check-out.
 *
 * Split out of FinalRoundModals.tsx to stay under the repo's 500-line limit.
 */
import { useState } from 'react';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { PeopleField, PersonField } from './PeopleField';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import type { FinalRoundConfigInput, FinalRoundSuggestion } from '../../services/taActionBoardApi';
import type { RoleBoard } from '../../types/taActionBoard';
import { FeedbackFormSelect } from '../interviewLinks/FeedbackFormSelect';
import { ModalActions } from './ModalActions';

/**
 * Interview lengths in QUARTER HOURS.
 *
 * These were `type="number"` inputs, so a 15-minute slot meant holding the
 * spinner down from 30 — and nothing stopped a 37-minute round. Interviews are
 * scheduled on a 15-minute raster (CHAIN_RASTER_MINUTES on the proxy), so the
 * options are the only lengths the solver can actually place.
 */
const MINUTE_OPTIONS = [15, 30, 45, 60, 75, 90];

function MinutesSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger label={label} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {MINUTE_OPTIONS.map((m) => (
          <SelectItem key={m} value={String(m)}>
            {m} min
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ConfigBody({
  role,
  suggestion,
  onConfirm,
  onCancel,
}: {
  role: RoleBoard;
  /**
   * Read off the kick-off sheet, never saved — a prefill for a role with no
   * template yet. The stored config always wins: once a TA manager has worked on
   * the template, a heuristic must not overwrite what they decided.
   */
  suggestion?: FinalRoundSuggestion | null;
  onConfirm: (config: FinalRoundConfigInput) => void;
  onCancel: () => void;
}) {
  const config = role.finalRounds.config;
  const [finalInterviewer, setFinalInterviewer] = useState(
    config?.finalInterviewer || suggestion?.finalInterviewer || ''
  );
  const [barRaiser, setBarRaiser] = useState(config?.barRaiser || suggestion?.barRaiser || '');
  const [minutes, setMinutes] = useState(
    String(config?.interviewMinutes || suggestion?.interviewMinutes || 30)
  );
  /**
   * The bar raiser's OWN length. One field used to set both, so a 45-minute
   * final interview forced a 45-minute bar raiser — and TA's real pattern is 45
   * + 30 (Theresa, Sep 2026). A template saved before the split has no
   * `barRaiserMinutes`, so it inherits `interviewMinutes` and opens showing the
   * length it was actually saved with.
   */
  const [barMinutes, setBarMinutes] = useState(
    String(config?.barRaiserMinutes || config?.interviewMinutes || suggestion?.interviewMinutes || 30)
  );
  const [g2k, setG2k] = useState((config?.g2k || []).join(', '));
  const [g2kMinutes, setG2kMinutes] = useState(String(config?.g2kMinutes || 30));
  const [hiringManager, setHiringManager] = useState(config?.hiringManager || '');
  // One Lever feedback form per real seat. Two fields rather than one because a
  // bar raiser assesses something the final interviewer does not — a shared form
  // would make the round's second opinion unrecordable.
  const [finalForm, setFinalForm] = useState(config?.finalFeedbackTemplate || '');
  const [barRaiserForm, setBarRaiserForm] = useState(config?.barRaiserFeedbackTemplate || '');
  // Optional recruiter check-out — a 15-min wrap-up the board owner runs by
  // default. The email field is an OOO stand-in only; empty means "the board
  // owner does it", resolved server-side.
  const [checkout, setCheckout] = useState(Boolean(config?.recruiterCheckout));
  const [checkoutEmail, setCheckoutEmail] = useState(config?.recruiterCheckoutEmail || '');
  const [checkoutForm, setCheckoutForm] = useState(config?.recruiterCheckoutFeedbackTemplate || '');

  // Was anything actually taken from the sheet? Only true for a role with no
  // stored template, which is the only time the suggestion is used at all.
  const prefilled = !config && !!(suggestion?.finalInterviewer || suggestion?.barRaiser);

  const isFinn = (e: string) => e.trim().toLowerCase().endsWith('@finn.com');
  const g2kParsed = g2k.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  const hmClean = hiringManager.trim().toLowerCase();
  const checkoutClean = checkoutEmail.trim().toLowerCase();
  const valid =
    isFinn(finalInterviewer) &&
    isFinn(barRaiser) &&
    Number(minutes) > 0 &&
    Number(barMinutes) > 0 &&
    Number(g2kMinutes) > 0 &&
    g2kParsed.every(isFinn) &&
    g2kParsed.length <= 4 &&
    (!hmClean || isFinn(hmClean)) &&
    // The stand-in is optional; only validate it when one was actually typed.
    (!checkout || !checkoutClean || isFinn(checkoutClean));

  return (
    <>
      <ModalHeader>
        <ModalTitle>Round template — {role.roleTitle}</ModalTitle>
      </ModalHeader>
      <div className="space-y-3">
        {/* Only when something was actually pre-filled — the old copy promised
            the kick-off sheet on every field while nothing was ever read. */}
        {prefilled && (
          <p className="rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-900">
            Pre-filled from the role's kick-off sheet — check the names and the length before
            saving. Nothing was saved yet.
          </p>
        )}
        <PersonField
          id="fr-final-interviewer"
          label="Final interviewer"
          value={finalInterviewer}
          onChange={setFinalInterviewer}
        />
        <PersonField
          id="fr-bar-raiser"
          label="Bar raiser"
          value={barRaiser}
          onChange={setBarRaiser}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <MinutesSelect label="Final interview length" value={minutes} onChange={setMinutes} />
          <MinutesSelect label="Bar raiser length" value={barMinutes} onChange={setBarMinutes} />
        </div>
        {/*
          The two seats become two separate interviews in Lever, each carrying
          its own form. No stage id is passed: a final round has no single Lever
          stage behind it, so there is no history to suggest from — the picker
          offers the list without a suggestion. There is deliberately NO field
          for the get-to-know: it is not an assessment, so it gets no form and
          no interview in Lever.
        */}
        <FeedbackFormSelect
          label="Feedback form — final interviewer"
          value={finalForm}
          postingId={role.postingId}
          stageId=""
          onChange={setFinalForm}
        />
        <FeedbackFormSelect
          label="Feedback form — bar raiser"
          value={barRaiserForm}
          postingId={role.postingId}
          stageId=""
          onChange={setBarRaiserForm}
        />
        <PeopleField
          id="fr-g2k"
          label="Get to know (optional, max 4 people)"
          value={g2k}
          onChange={setG2k}
        />
        {g2kParsed.length > 0 && (
          <div className="space-y-1.5 rounded-lg border border-gray-200 p-3">
            <MinutesSelect
              label="Get-to-know length"
              value={g2kMinutes}
              onChange={setG2kMinutes}
            />
            <p className="text-xs text-gray-500">
              Everyone above meets the candidate together in ONE block — no individual coffee
              chats, so the round stays at three conversations at most.
            </p>
          </div>
        )}
        {/* Who collects the candidate onsite is chosen per candidate in the
            booking screen, not here — the recruiter is the default. Adding a
            hiring manager here is what lets the booking screen offer them as the
            onsite contact. */}
        <PersonField
          id="fr-hiring-manager"
          label="Hiring manager (optional)"
          value={hiringManager}
          onChange={setHiringManager}
          hint="Lets you pick them as the onsite contact when booking."
        />
        {/* Optional recruiter check-out — always placed last in the chain by the
            solver. A real Lever seat (unlike the get-to-know), so it can carry a
            feedback form. */}
        <div className="space-y-2 rounded-lg border border-gray-200 p-3">
          <Checkbox
            label="Recruiter check-out (optional, 15 min)"
            labelClassName="text-sm text-gray-700"
            checked={checkout}
            onCheckedChange={(v) => setCheckout(v === true)}
          />
          {checkout && (
            <>
              <p className="text-xs text-gray-500">
                A 15-minute wrap-up at the end of the day, run by the board owner. Add someone else
                only to cover when the owner is out of office.
              </p>
              <PersonField
                id="fr-checkout-standin"
                label="Runs as (optional stand-in)"
                value={checkoutEmail}
                onChange={setCheckoutEmail}
                hint="Defaults to the board owner."
              />
              <FeedbackFormSelect
                label="Feedback form — recruiter check-out"
                value={checkoutForm}
                postingId={role.postingId}
                stageId=""
                onChange={setCheckoutForm}
              />
            </>
          )}
        </div>
      </div>
      <ModalActions
        confirmLabel="Save template"
        confirmDisabled={!valid}
        onConfirm={() =>
          onConfirm({
            finalInterviewer: finalInterviewer.trim().toLowerCase(),
            barRaiser: barRaiser.trim().toLowerCase(),
            interviewMinutes: Number(minutes),
            barRaiserMinutes: Number(barMinutes),
            g2k: g2kParsed,
            g2kMinutes: Number(g2kMinutes),
            hiringManager: hmClean || null,
            finalFeedbackTemplate: finalForm || null,
            barRaiserFeedbackTemplate: barRaiserForm || null,
            recruiterCheckout: checkout,
            recruiterCheckoutEmail: checkout ? checkoutClean || null : null,
            recruiterCheckoutFeedbackTemplate: checkout ? checkoutForm || null : null,
          })
        }
        onCancel={onCancel}
      />
    </>
  );
}
